import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import { Button, Icon } from 'semantic-ui-react'
import styled from 'styled-components'

import PageHeader from '../../components/pageHeader/pageHeader'
import PagedTable from '../../components/dataTable/pagedTable'
import { Recruiter } from '../../types'
import RecruiterSidePanel from './RecruiterSidePanel'

const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
}).format

const PageContent = styled.div`
  margin: 0px 50px 50px 50px;
`

export default function RecruitersSettingsPage() {
  const [panelState, setPanelState] = useState<{
    open: boolean
    recruiterId?: string | null
  }>({ open: false, recruiterId: null })

  const [tableRefreshKey, setTableRefreshKey] = useState(0)

  const handleAddRecruiter = () => {
    setPanelState({ open: true, recruiterId: null })
  }

  const handleEditRecruiter = (recruiter: Recruiter) => {
    setPanelState({ open: true, recruiterId: recruiter._id })
  }

  const handleClosePanel = () => {
    setPanelState({ open: false, recruiterId: null })
  }

  const handleReloadTable = () => {
    setTableRefreshKey((prev) => prev + 1)
  }

  const handleSaved = () => {
    handleReloadTable()
    handleClosePanel()
  }

  const handleDeleted = () => {
    handleReloadTable()
    handleClosePanel()
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Settings &gt; Recruiters</title>
      </Helmet>

      <PageHeader
        breadcrumb={[
          { text: 'Dashboard', link: '/' },
          { text: 'Settings', link: '/settings' },
          { text: 'Recruiters', link: '/settings/recruiters' },
        ]}
        title="Recruiters"
        sub="Manage the recruiters you work with."
        actions={
          <Button basic onClick={handleAddRecruiter}>
            <Icon name="add" /> Add Recruiter
          </Button>
        }
      />

      <PageContent>
        <PagedTable<Recruiter>
          key={tableRefreshKey}
          collection="recruiters"
          defaultSort="lastUpdateOn"
          onRowClick={handleEditRecruiter}
          columns={[
            {
              title: 'Name',
              field: 'name',
              width: 260,
            },
            {
              title: 'Email',
              field: 'email',
              width: 320,
            },
            {
              title: 'Payment Term',
              field: 'paymentTerm',
              width: 140,
              render: (paymentTerm: number | null | undefined) =>
                typeof paymentTerm === 'number' ? `${paymentTerm} days` : '—',
            },
            {
              title: 'Fee Cap',
              field: 'feeCap',
              width: 150,
              render: (feeCap: number | null | undefined) =>
                typeof feeCap === 'number' ? currencyFormat(feeCap) : '—',
            },
            {
              title: 'Notes',
              field: 'notes',
              width: 280,
              render: (notes: string | null | undefined) =>
                notes ? (
                  <div className="line-clamp-2 max-w-[280px] whitespace-pre-wrap text-slate-600">
                    {notes}
                  </div>
                ) : (
                  '—'
                ),
            },
          ]}
        />

        <RecruiterSidePanel
          open={panelState.open}
          recruiterId={panelState.recruiterId ?? undefined}
          onClose={handleClosePanel}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      </PageContent>
    </React.Fragment>
  )
}
